import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import heroBg from "../assets/Ebookbg.jpg";

const HeroSection = () => {
  const navigate = useNavigate();
  
  // CONFIGURATION
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzoOu3omBoH_inj9IXstqd2EGcqnne3ej6JCaxjsW1SaNarwL4BmS2gyszZlyfgGow/exec';
  const DOWNLOAD_FILE_URL = '/E-book.pdf';

  // FORM STATE
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    companyName: '',
    pageUrl: '' // This will store the present site URL
  });

  // Capture the Present Site URL on Load
  useEffect(() => {
    setFormData(prev => ({ 
      ...prev, 
      pageUrl: window.location.href 
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const onlyNums = value.replace(/[^0-9]/g, '');
      if (onlyNums.length <= 10) setFormData({ ...formData, [name]: onlyNums });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // STEP 1: SAVE PARTIAL DATA & GO TO NEXT
  const handleNext = async (e) => {
    e.preventDefault();
    if (formData.name && formData.email) {
      setStep(2);
      
      // LEAD RECOVERY: Save partial lead to Google Sheet in background
      try {
        fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, status: 'Partial' }),
        });
      } catch (err) {
        console.error("Partial save failed");
      }
    } else {
      alert("Please fill in your name and email.");
    }
  };

  // STEP 2: SAVE FINAL DATA & DOWNLOAD
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!formData.companyName) {
      alert("Company Name is required.");
      return;
    }

    setStatus('loading');
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, status: 'Complete' }),
      });

      const link = document.createElement('a');
      link.href = DOWNLOAD_FILE_URL;
      link.setAttribute('download', 'The-Startup-Zone-Ebook.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();

      navigate('/thankyou');
    } catch (error) {
      console.error("Error:", error);
      setStatus('idle');
      alert("Submission failed. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        .hc-section {
          position: relative;
          min-height: 100vh;
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center; /* Vertical Center */
          align-items: center;     /* Horizontal Center */
          padding: 80px 1.25rem;   /* Fixed gaps top and bottom */
          overflow: hidden;
          background-size: cover;
          background-position: center;
          background-color: #0b1120;
          box-sizing: border-box;
        }

        .hc-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hc-content { 
          text-align: center; 
          max-width: 48rem; 
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center; 
        }

        .hc-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          border: 1px solid hsla(171, 76%, 40%, 0.4);
          background: hsla(171, 76%, 40%, 0.1);
          padding: 0.35rem 1rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: hsl(171, 76%, 40%);
          margin-bottom: 1.5rem; 
          letter-spacing: 0.05em;
          width: fit-content;
        }

        .hc-title {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.15;
          color: white;
          margin: 0 0 1.25rem 0;
        }

        .hc-text-gradient {
          background: linear-gradient(to right, #2dd4bf, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hc-description {
          font-size: 1.1rem;
          color: #94a3b8;
          max-width: 42rem;
          margin: 0 auto 2.5rem auto;
          line-height: 1.6;
        }

        .hc-tf-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          padding: 2rem;
          width: 100%;
          max-width: 36rem;
          margin: 0 auto;
          text-align: left;
        }

        .hc-tf-step-label {
          color: #2dd4bf;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 1.25rem;
          display: block;
        }

        .hc-tf-row {
          display: flex;
          gap: 1rem;
          width: 100%;
        }

        .hc-tf-input-group { 
          margin-bottom: 1.25rem; 
          flex: 1; 
        }
        
        .hc-tf-input-group label {
          display: block;
          color: #f8fafc;
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .hc-tf-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: white;
          outline: none;
          box-sizing: border-box;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }

        .hc-tf-input:focus { border-color: #2dd4bf; }

        .hc-tf-btn {
          width: 100%;
          padding: 1rem;
          background: #2dd4bf;
          color: #0b1120;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.2s, opacity 0.2s;
        }

        .hc-tf-btn:hover { transform: translateY(-1px); opacity: 0.95; }

        .hc-features-list {
          margin-top: 2.5rem;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 1rem 1.5rem;
          font-size: 0.75rem;
          color: #94a3b8;
        }

        .hc-feature-item { display: flex; align-items: center; gap: 0.5rem; }
        .hc-dot { width: 0.25rem; height: 0.25rem; border-radius: 50%; background: #2dd4bf; }

        @media (min-width: 768px) {
          .hc-title { font-size: 3.5rem; }
          .hc-tf-card { padding: 3rem; }
        }

        @media (max-width: 600px) {
          .hc-tf-row { flex-direction: column; gap: 0; }
          .hc-title { font-size: 1.8rem; }
          .hc-section { padding: 60px 1rem; }
        }
      `}</style>

      <section
        className="hc-section"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(11, 17, 32, 0.92), rgba(11, 17, 32, 0.96)), url(${heroBg})`,
        }}
      >
        <div className="hc-container">
          <div className="hc-content">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="hc-badge"
            >
              Free · Indian Founders Only
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="hc-title"
            >
              You started a company.{" "}
              <span className="hc-text-gradient">Do you actually own it?</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="hc-description"
            >
              Most Indian founders figure out the legal side after something goes wrong. 
              This ebook ensures you don't learn it the expensive way.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="hc-tf-card"
            >
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.form
                    key="step1"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -20, opacity: 0 }}
                    onSubmit={handleNext}
                  >
                    <span className="hc-tf-step-label">Step 1 of 2: Basics</span>
                    <div className="hc-tf-row">
                      <div className="hc-tf-input-group">
                        <label>First Name</label>
                        <input 
                          required 
                          name="name" 
                          value={formData.name} 
                          onChange={handleChange} 
                          className="hc-tf-input" 
                          placeholder="Name"
                        />
                      </div>
                      <div className="hc-tf-input-group">
                        <label>Email Address</label>
                        <input 
                          required 
                          type="email" 
                          name="email" 
                          value={formData.email} 
                          onChange={handleChange} 
                          className="hc-tf-input" 
                          placeholder="Email"
                        />
                      </div>
                    </div>
                    <button type="submit" className="hc-tf-btn">Next</button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="step2"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -20, opacity: 0 }}
                    onSubmit={handleSubmit}
                  >
                    <span className="hc-tf-step-label">Step 2 of 2: Company Info</span>
                    <div className="hc-tf-row">
                      <div className="hc-tf-input-group">
                        <label>Mobile Number</label>
                        <input 
                          required 
                          name="mobile" 
                          value={formData.mobile} 
                          onChange={handleChange} 
                          className="hc-tf-input" 
                          placeholder="10-digits"
                        />
                      </div>
                      <div className="hc-tf-input-group">
                        <label>Company Name</label>
                        <input 
                          required 
                          name="companyName" 
                          value={formData.companyName} 
                          onChange={handleChange} 
                          className="hc-tf-input" 
                          placeholder="Company Name"
                        />
                      </div>
                    </div>
                    <button type="submit" className="hc-tf-btn" disabled={status === 'loading'}>
                      {status === 'loading' ? 'Processing...' : 'Submit & Download'}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setStep(1)} 
                      style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.75rem', marginTop: '1.25rem', cursor: 'pointer', width: '100%', textAlign: 'center' }}
                    >
                      ← Back
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="hc-features-list"
            >
              {["10 chapters", "100% India-specific", "₹0 forever free", "Written for founders"].map((item) => (
                <span key={item} className="hc-feature-item">
                  <span className="hc-dot" />
                  {item}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;